/**
 * Godot's lightmap read in three's lit programs: a lightmapped surface's ambient is its texel of the
 * atlas, at its UV2 placed by its user rect and slice (`scene_forward_clustered.glsl:1835-1876`).
 * Scene and sky ambient give way to it (`:1739-1759`), and so does a ReflectionProbe's
 * (`probeShaderChunk.ts`). three's irradiance is Godot's ambient times PI. The bake's shadowmask
 * replaces or darkens a sun's real-time shadow at the same UV2 (`:2252-2273`, `:2483-2492`).
 */
import * as THREE from 'three';
import { LIGHTMAP_SH_LAYERS_PER_SLICE, ShadowmaskMode } from '../../godot/scene/lightmapGi';
import { composeInjections, defining } from '../materialProgramInputs';
import { editedProgram } from '../shaderPatch/chunkPatch';
import { bicubicFilterGlsl } from '../../utils/bicubicFilterGlsl';
/**
 * A light whose shadow opacity is at most this floor skips its shadow block, shadowmask included
 * (`scene_forward_clustered.glsl:2298`). Mode Only has no such skip (`:2510-2517`).
 */
const SHADOW_OPACITY_FLOOR = '0.001';
/** The last declaration before `main`. */
const PARS_ANCHOR = '#include <clipping_planes_pars_fragment>';
const MAPS_ANCHOR = '#include <lights_fragment_maps>';
/**
 * Godot's bicubic B-spline filter, four bilinear taps (`:921-978`), and its read of a slice: one
 * layer of light, or four of directional light whose L1 bands it lights along the normal in the
 * LightmapGI's space. Only L0 takes the exposure scale, as the bands light through `l0 * en * 4`
 * (`:1863-1868`).
 */
const LIGHTMAP_PARS = /* glsl */ `${PARS_ANCHOR}
uniform bool godotLightmapActive;
uniform highp sampler2DArray godotLightmap;
uniform vec4 godotLightmapUvScale;
uniform float godotLightmapSlice;
uniform float godotLightmapExposure;
uniform bool godotLightmapUsesSH;
uniform bool godotLightmapUsesBicubic;
uniform vec2 godotLightmapTextureSize;
uniform mat3 godotLightmapNormalBasis;
uniform int godotShadowmaskMode;
varying vec2 vGodotLightmapUv;

${bicubicFilterGlsl({
    prefix: 'godotLightmap',
    params: 'vec3 uvw',
    uv: 'uvw.xy',
    size: 'godotLightmapTextureSize',
    fetch: (texelUv) => `texture( godotLightmap, vec3( ${texelUv}, uvw.z ) ).rgb`,
})}
vec3 godotLightmapTexel( vec3 uvw ) {
	return godotLightmapUsesBicubic ? godotLightmapBicubic( uvw ) : textureLod( godotLightmap, uvw, 0.0 ).rgb;
}

${bicubicFilterGlsl({
    prefix: 'godotShadowmask',
    params: 'vec3 uvw',
    uv: 'uvw.xy',
    // Godot sizes the mask with the lightmap's texture size (`scene_forward_clustered.glsl:2268`).
    size: 'godotLightmapTextureSize',
    fetch: (texelUv) => `vec3( texture( godotLightmap, vec3( ${texelUv}, uvw.z ) ).a )`,
})}
vec3 godotLightmapUvw() {
	float layer = godotLightmapSlice * ( godotLightmapUsesSH ? ${LIGHTMAP_SH_LAYERS_PER_SLICE}.0 : 1.0 );
	return vec3( vGodotLightmapUv * godotLightmapUvScale.zw + godotLightmapUvScale.xy, layer );
}

vec3 godotLightmapAmbient( vec3 worldNormal ) {
	vec3 uvw = godotLightmapUvw();
	vec3 l0 = godotLightmapTexel( uvw ) * godotLightmapExposure;
	if ( ! godotLightmapUsesSH ) return l0;
	vec3 l1n1 = ( godotLightmapTexel( uvw + vec3( 0.0, 0.0, 1.0 ) ) - vec3( 0.5 ) ) * 2.0;
	vec3 l1z = ( godotLightmapTexel( uvw + vec3( 0.0, 0.0, 2.0 ) ) - vec3( 0.5 ) ) * 2.0;
	vec3 l1p1 = ( godotLightmapTexel( uvw + vec3( 0.0, 0.0, 3.0 ) ) - vec3( 0.5 ) ) * 2.0;
	vec3 n = normalize( godotLightmapNormalBasis * worldNormal );
	return l0 + l1n1 * n.y * ( l0 * 4.0 ) + l1z * n.z * ( l0 * 4.0 ) + l1p1 * n.x * ( l0 * 4.0 );
}

float godotLightmapShadowmask() {
	vec3 uvw = godotLightmapUvw();
	return godotLightmapUsesBicubic ? godotShadowmaskBicubic( uvw ).x : textureLod( godotLightmap, uvw, 0.0 ).a;
}

float godotShadowmaskReplace( float shadow, float shadowmask, float fadeOut, float opacity ) {
	return mix( shadow, mix( 1.0, shadowmask, opacity ), fadeOut );
}

float godotShadowmaskOverlay( float shadow, float shadowmask, float fadeOut, float opacity ) {
	float unshadowed = 1.0 - opacity;
	return unshadowed + shadowmask * ( ( 1.0 - fadeOut ) * ( shadow - unshadowed ) + opacity * fadeOut );
}

float godotShadowmaskOnly( float shadowmask, float isDrawnFirst, float opacity ) {
	float shadow = isDrawnFirst * trunc( clamp( shadowmask * 255.0, 0.0, 255.0 ) ) / 255.0;
	return mix( 1.0, shadow, opacity );
}

#if defined( USE_SHADOWMAP ) && NUM_SUN_LIGHT_SHADOWS > 0
float godotLightmapSunShadow( float shadow, float fadeOut, float opacity, float isDrawnFirst ) {
	if ( ! godotLightmapActive || godotShadowmaskMode == ${ShadowmaskMode.NONE} ) return godotSunShadowFaded( shadow, fadeOut );
	if ( godotShadowmaskMode == ${ShadowmaskMode.ONLY} ) return godotShadowmaskOnly( godotLightmapShadowmask(), isDrawnFirst, opacity );
	if ( opacity <= ${SHADOW_OPACITY_FLOOR} ) return 1.0;
	float shadowmask = godotLightmapShadowmask();
	return godotShadowmaskMode == ${ShadowmaskMode.REPLACE}
		? godotShadowmaskReplace( shadow, shadowmask, fadeOut, opacity )
		: godotShadowmaskOverlay( shadow, shadowmask, fadeOut, opacity );
}
#endif`;
/** After three's maps have added the sky's irradiance, which the lightmap replaces. */
const LIGHTMAP_AMBIENT = /* glsl */ `${MAPS_ANCHOR}
if ( godotLightmapActive ) {
	irradiance = PI * godotLightmapAmbient( inverseTransformDirection( geometryNormal, viewMatrix ) );
	iblIrradiance = vec3( 0.0 );
}`;
/** Godot's UV2, three's `uv1`, which three declares only while a map reads it. */
const LIGHTMAP_UV_PARS = /* glsl */ `#include <uv_pars_vertex>
#ifndef USE_UV1
	attribute vec2 uv1;
#endif
varying vec2 vGodotLightmapUv;`;
const LIGHTMAP_UV = /* glsl */ `#include <uv_vertex>
vGodotLightmapUv = uv1;`;
/**
 * Each material's assignment. Written only by `bindLightmap`, and collected with its material. Not
 * `userData`, which a material's clone copies through JSON.
 */
// eslint-disable-next-line no-restricted-syntax -- bindLightmap writes or deletes an entry from outside
const assignments = new WeakMap();
/**
 * Whether a surface reads its lightmap: only a lit one, and only in the opaque pass, which is the
 * only pass that reads a lightmap (`render_forward_clustered.cpp:985-1003`).
 */
export function readsLightmap(isLit, isTransparent) {
    return isLit && !isTransparent;
}
/** Gives `material` the lightmap it reads, or none. A rebind needs no recompile. */
export function bindLightmap(material, assignment) {
    if (assignment)
        assignments.set(material, assignment);
    else
        assignments.delete(material);
}
/** Scratch for one upload: three reads each value as it uploads the material. */
const uvScale = new THREE.Vector4();
const textureSize = new THREE.Vector2();
const normalBasis = new THREE.Matrix3();
/** Uniforms that read `material`'s assignment at each upload, so a moved LightmapGI needs no remount. */
function lightmapUniforms(material) {
    const bound = () => assignments.get(material);
    return {
        godotLightmapActive: {
            get value() {
                return bound()?.lightmap.isDrawn ?? false;
            },
        },
        // three binds its empty array texture for null.
        godotLightmap: {
            get value() {
                return bound()?.lightmap.atlas ?? null;
            },
        },
        godotLightmapUvScale: {
            get value() {
                const rect = bound()?.user.uvScale;
                return rect ? uvScale.set(rect.x, rect.y, rect.width, rect.height) : uvScale.set(0, 0, 0, 0);
            },
        },
        godotLightmapSlice: {
            get value() {
                return bound()?.user.sliceIndex ?? 0;
            },
        },
        godotLightmapExposure: {
            get value() {
                return bound()?.lightmap.exposureScale ?? 1;
            },
        },
        godotLightmapUsesSH: {
            get value() {
                return bound()?.lightmap.usesSphericalHarmonics ?? false;
            },
        },
        godotLightmapUsesBicubic: {
            get value() {
                return bound()?.lightmap.usesBicubicFilter ?? false;
            },
        },
        godotLightmapTextureSize: {
            get value() {
                const image = bound()?.lightmap.atlas?.image;
                return image ? textureSize.set(image.width, image.height) : textureSize.set(1, 1);
            },
        },
        // `(L⁻¹ C)⁻ᵀ` on a view normal (`render_forward_clustered.cpp:1213-1215`) is `Lᵀ` on the world normal.
        godotLightmapNormalBasis: {
            get value() {
                const object = bound()?.lightmap.object;
                return object ? normalBasis.setFromMatrix4(object.matrixWorld).transpose() : normalBasis.identity();
            },
        },
        // A bake with no shadowmask binds Godot a white array, which mixes as None in both modes.
        godotShadowmaskMode: {
            get value() {
                return bound()?.lightmap.shadowmaskMode ?? ShadowmaskMode.NONE;
            },
        },
    };
}
const LIGHTMAP_READ = {
    cacheKey: 'godot-lightmap-read',
    onBeforeCompile(shader) {
        Object.assign(shader.uniforms, lightmapUniforms(this));
        editedProgram(shader, {
            vertex: [
                { three: '#include <uv_pars_vertex>', godot: LIGHTMAP_UV_PARS },
                { three: '#include <uv_vertex>', godot: LIGHTMAP_UV },
            ],
            fragment: [
                { three: PARS_ANCHOR, godot: LIGHTMAP_PARS },
                { three: MAPS_ANCHOR, godot: LIGHTMAP_AMBIENT },
            ],
        }, 'lightmap read');
    },
};
/**
 * What a lightmapped lit material compiles with. `GODOT_LIGHTMAP` also turns on the Static light
 * skip (`lightBakeModeChunk.ts`) and the probe ambient guard.
 */
export const LIGHTMAP_PROGRAM = composeInjections([
    defining('GODOT_LIGHTMAP'),
    LIGHTMAP_READ,
]);
//# sourceMappingURL=lightmapProgram.js.map